import { useFormikContext } from 'formik';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import type { DataSourceFieldSchemaValues } from '@/zod/schemas/dataSource/DataSourceField';
import {
  FormDropdown,
  FormError,
  FormInput,
  FormSqlExpressionInput,
} from '@/components/form/form';
import { FormDatabricksPathAndTableValidatedInput } from '@/components/form/input/FormDatabricksPathAndTableValidatedInput';

interface JoinTabProps {
  currentTable: string;
  currentTableFields: string[];
}

export default function JoinTab({
  currentTableFields,
  currentTable,
}: JoinTabProps) {
  const { handleBlur, handleChange, values, setFieldValue } =
    useFormikContext<DataSourceFieldSchemaValues>();

  return (
    <>
      <Spacer size="lg" />

      <ButtonGroup
        options={[
          { label: 'Table', value: 'table' },
          { label: 'CSV', value: 'csv' },
        ]}
        onChange={(value) => setFieldValue('join.useCSV', value === 'csv')}
        value={values.join?.useCSV ? 'csv' : 'table'}
      />

      <Spacer size="lg" />

      {!values.join?.useCSV && (
        <>
          <Label htmlFor="join.withTable" id="join.withTable-label" required>
            Table to join to
          </Label>
          <FormDatabricksPathAndTableValidatedInput
            id="join.withTable"
            name="join.withTable"
            placeholder="Enter a table name"
            constraint="mustExistRead"
            validationType="table"
            tableSchema={
              currentTable === 'silver' ? 'silverSchema' : 'goldSchema'
            }
            required
          />
          <FormError name="join.withTable" />
        </>
      )}

      {values.join?.useCSV && (
        <>
          <Label
            htmlFor="join.withCSV.path"
            id="join.withCSV.path-label"
            required
          >
            CSV file path
          </Label>
          <FormDatabricksPathAndTableValidatedInput
            id="join.withCSV.path"
            name="join.withCSV.path"
            placeholder="Enter a CSV file path"
            constraint="mustExistRead"
            validationType="path"
            required
          />
          <FormError name="join.withCSV.path" />
        </>
      )}

      <Spacer size="lg" />

      <div className="flex w-auto gap-2">
        <div className="flex-1">
          <Label htmlFor="join.lhs" id="join.lhs-label" required>
            LHS column
          </Label>
          <FormDropdown
            name="join.lhs"
            options={currentTableFields}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.join?.lhs}
            aria-labelledby="join.lhs-label"
            placeholder="Select a column to join on"
          />
          <FormError name="join.lhs" />
        </div>
        <div className="flex-1">
          <Label htmlFor="join.rhs" id="join.rhs-label" required>
            RHS column
          </Label>
          <FormInput
            id="join.rhs"
            name="join.rhs"
            placeholder={`Enter a column in ${
              values.join?.useCSV
                ? 'the CSV file'
                : values.join?.withTable
                  ? `"${values.join?.withTable}"`
                  : 'the table'
            } to join on`}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.join?.rhs}
            aria-labelledby="join.rhs-label"
          />
          <FormError name="join.rhs" />
        </div>
      </div>

      <Spacer size="lg" />

      <Label htmlFor="join.select" id="join.select-label" required>
        Enter field value expression
      </Label>
      <FormSqlExpressionInput
        name="join.select"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.join?.select}
        aria-labelledby="join.select-label"
      />
      <FormError name="join.select" />
    </>
  );
}
